import { type Editor, Extension, type Range } from '@tiptap/core'
import { PluginKey } from '@tiptap/pm/state'
import { Suggestion, type SuggestionOptions } from '@tiptap/suggestion'
import { type LucideIcon } from 'lucide-react'
import { isComposing } from '@/lib/ime'

// Suggestion lists in a card: `@` points at something on the board or in a
// material, `/` inserts a block. The plugin (@tiptap/suggestion) follows what
// is typed after the trigger; the list itself is React, fed through a store
// so that it never re-renders the editor. While an IME is composing, keys
// belong to the IME: the list never takes Enter, arrows, Esc or Tab then.

export type SuggestItem = {
  key: string
  icon: LucideIcon
  label: string
  /** A second, quieter line: where the thing is, or what the block is. */
  detail?: string
  run: (editor: Editor, range: Range) => void
}

type Open = {
  items: SuggestItem[]
  active: number
  rect: DOMRect | null
  pick: (item: SuggestItem) => void
  /** A label for the list, read by screen readers. */
  label: string
  empty: string
}

/** What the list shows; one per editor. */
export class SuggestStore {
  private state: Open | null = null
  private readonly listeners = new Set<() => void>()
  subscribe = (fn: () => void) => {
    this.listeners.add(fn)
    return () => this.listeners.delete(fn)
  }
  getSnapshot = () => this.state
  set(next: Open | null) {
    this.state = next
    for (const fn of this.listeners) fn()
  }
  move(by: number) {
    const s = this.state
    if (!s?.items.length) return
    this.set({ ...s, active: (s.active + by + s.items.length) % s.items.length })
  }
}

type Source = {
  /** The character that opens the list. */
  char: string
  name: string
  store: SuggestStore
  items: (query: string, editor: Editor) => SuggestItem[]
  label: () => string
  empty: () => string
  /** Whether the list may open here (`textBefore` is the text just before the trigger). */
  allowAfter: (textBefore: string) => boolean
  allowSpaces?: boolean
}

/** A suggestion list opened by `char`, shown by <SuggestList store={…} />. */
export function suggestExtension(src: Source) {
  return Extension.create({
    name: src.name,
    // Ahead of the editor's own keys (Enter, arrows), and of a message's Enter-to-place.
    priority: 1000,
    addProseMirrorPlugins() {
      const options: SuggestionOptions<SuggestItem, SuggestItem> = {
        editor: this.editor,
        pluginKey: new PluginKey(src.name),
        char: src.char,
        allowSpaces: src.allowSpaces ?? false,
        allowedPrefixes: null,
        startOfLine: false,
        allow: ({ state, range }) => {
          const $from = state.doc.resolve(range.from)
          // Never inside code, and never in the middle of a word or an address.
          if ($from.parent.type.spec.code) return false
          const before = $from.parent.textBetween(0, $from.parentOffset, '\n', ' ')
          return src.allowAfter(before)
        },
        items: ({ query, editor }) => src.items(query, editor),
        command: ({ editor, range, props }) => {
          props.run(editor, range)
        },
        render: () => {
          let pick: ((item: SuggestItem) => void) | null = null
          const show = (p: {
            items: SuggestItem[]
            clientRect?: (() => DOMRect | null) | null
            command: (item: SuggestItem) => void
          }) => {
            pick = p.command
            const cur = src.store.getSnapshot()
            src.store.set({
              items: p.items,
              active: cur ? Math.min(cur.active, Math.max(0, p.items.length - 1)) : 0,
              rect: p.clientRect?.() ?? null,
              pick: (item) => pick?.(item),
              label: src.label(),
              empty: src.empty(),
            })
          }
          return {
            onStart: (p) => {
              src.store.set(null)
              show(p)
            },
            onUpdate: show,
            onExit: () => {
              src.store.set(null)
            },
            onKeyDown: ({ event }) => {
              if (isComposing(event)) return false
              const s = src.store.getSnapshot()
              if (!s) return false
              if (event.key === 'Escape') {
                src.store.set(null)
                return true
              }
              if (!s.items.length) return false
              if (event.key === 'ArrowDown') {
                src.store.move(1)
                return true
              }
              if (event.key === 'ArrowUp') {
                src.store.move(-1)
                return true
              }
              if (event.key === 'Enter' || event.key === 'Tab') {
                const item = s.items[s.active]
                if (item) s.pick(item)
                return true
              }
              return false
            },
          }
        },
      }
      return [Suggestion(options)]
    },
  })
}
